/**
 * Cart Custom Fee Embroidery Processor - 购物车定制费刺绣工艺处理器
 * 刺绣工艺的独立实现，逻辑与DTF相同，使用不同的商品变体
 */

// EMBROIDERY 工艺处理器
class EmbroideryProcessor extends CraftProcessor {
  constructor() {
    super({
      processType: 'EMBROIDERY',
      productHandle: 'custom-embroidery-fee',
      productTitle: 'Custom Embroidery Fee',
      ranges: [
        { min: 1, max: 11, variantId: 46024065515673 },
        { min: 12, max: 47, variantId: 46024065548441 },
        { min: 48, max: 143, variantId: 46024065581209 },
        { min: 144, max: 287, variantId: 46024065613977 },
        { min: 288, max: Infinity, variantId: 46024065646745 }
      ]
    });
    
    console.log('[Embroidery Processor] ✅ 刺绣处理器已初始化', {
      processType: 'EMBROIDERY',
      ranges: this.ranges
    });
  }
  
  /**
   * 绣花工艺核心业务逻辑 - 计算所需的定制费用项（完全复制DTF逻辑）
   * @param {Array} customProducts - 定制商品列表 [{quantity, customSides, draftOrderId, product}]
   * @returns {Array} - 需要添加的费用项列表
   */
  calculateRequiredFeeItems(customProducts) {
    if (!customProducts || customProducts.length === 0) {
      return [];
    }

    console.log('[Embroidery Processor] 开始计算绣花定制费用', { customProducts });
    
    // 详细检查输入数据
    customProducts.forEach((product, index) => {
      console.log(`[Embroidery Processor] 🔍 商品${index + 1}详细信息:`, {
        quantity: product.quantity,
        customSides: product.customSides,
        customSidesType: typeof product.customSides,
        customSidesLength: product.customSides?.length,
        groupId: product.groupId,
        draftOrderId: product.draftOrderId,
        productTitle: product.product?.title
      });
    });
    
    // 按group_id分组处理
    const groupedByGroup = this.groupByGroupId(customProducts);
    console.log('[Embroidery Processor] 按group_id分组:', groupedByGroup);
    
    // 为每个group独立计算费用项
    const allFeeItems = [];
    
    Object.entries(groupedByGroup).forEach(([groupId, products]) => {
      console.log(`[Embroidery Processor] 处理group: ${groupId}`, products);
      
      // 计算该group的统计信息
      const groupStats = this.calculateGroupStats(products);
      console.log(`[Embroidery Processor] group${groupId}统计:`, groupStats);
      
      // 根据该group的数量确定价格区间
      const priceRange = this.getPriceRangeByQuantity(groupStats.totalQuantity);
      console.log(`[Embroidery Processor] 💰 group${groupId}价格区间:`, priceRange);
      
      // 计算该group的实际收费数量
      const billingQuantity = this.calculateBillingQuantity(groupStats.totalQuantity, priceRange);
      console.log(`[Embroidery Processor] 🧮 group${groupId}计费数量: 实际${groupStats.totalQuantity} → 计费${billingQuantity}`);
      
      // 为该group生成费用项
      const groupFeeItems = this.generateGroupFeeItems(
        groupId,
        billingQuantity, 
        groupStats, 
        products
      );
      console.log(`[Embroidery Processor] 📋 group${groupId}生成${groupFeeItems.length}个费用项:`, groupFeeItems);
      
      allFeeItems.push(...groupFeeItems);
    });
    
    console.log(`[Embroidery Processor] ✅ 所有group处理完成，总共生成${allFeeItems.length}个费用项:`, allFeeItems);
    
    // 检查每个费用项的关键数据
    allFeeItems.forEach((item, index) => {
      console.log(`[Embroidery Processor] 费用项${index + 1}:`, {
        variantId: item.id,
        quantity: item.quantity,
        side: item.properties._custom_side,
        groupId: item.properties._group_id,
        description: item.properties._description
      });
    });
    
    return allFeeItems;
  }
  
  /**
   * 按group_id分组
   */
  groupByGroupId(customProducts) {
    const groups = {};
    
    customProducts.forEach(product => {
      const groupId = product.groupId; // Manager已经解析好的groupId
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(product);
    });
    
    return groups;
  }
  
  /**
   * 计算单个group的统计信息
   */
  calculateGroupStats(groupProducts) {
    let totalQuantity = 0;
    const allCustomSides = new Set();
    const productDetails = [];
    let draftOrderId = null;
    
    groupProducts.forEach(item => {
      totalQuantity += item.quantity;
      
      // 获取草稿单ID（用于后续属性标记）
      if (!draftOrderId) {
        draftOrderId = item.draftOrderId;
      }
      
      // 收集定制面信息
      console.log(`[Embroidery Processor] 🔍 处理商品定制面:`, {
        customSides: item.customSides,
        customSidesType: typeof item.customSides,
        isArray: Array.isArray(item.customSides)
      });
      
      if (item.customSides && Array.isArray(item.customSides)) {
        item.customSides.forEach(side => {
          console.log(`[Embroidery Processor] 添加定制面: ${side}`);
          allCustomSides.add(side);
        });
      }
      
      // 记录产品详情
      productDetails.push({
        quantity: item.quantity,
        customSides: item.customSides || [],
        draftOrderId: item.draftOrderId,
        groupId: item.groupId,
        productTitle: item.product?.title || 'Unknown Product'
      });
    });
    
    console.log(`[Embroidery Processor] 📊 统计结果:`, {
      totalQuantity,
      uniqueCustomSides: Array.from(allCustomSides),
      customSideCount: allCustomSides.size,
      allCustomSidesSet: allCustomSides
    });
    
    return {
      totalQuantity,
      uniqueCustomSides: Array.from(allCustomSides),
      customSideCount: allCustomSides.size,
      productDetails,
      draftOrderId // 保留草稿单ID用于属性标记
    };
  }
  
  /**
   * 为单个group生成费用项
   */
  generateGroupFeeItems(groupId, billingQuantity, groupStats, groupProducts) {
    const feeItems = [];
    const { uniqueCustomSides, productDetails, draftOrderId, totalQuantity } = groupStats;
    
    // 检查是否需要Setup Fee（绣花工艺在第一个区间1-11时需要）
    if (this.needsSetupFee(totalQuantity)) {
      const setupFeeItem = this.createSetupFeeItem(totalQuantity, groupId, draftOrderId);
      if (setupFeeItem) {
        feeItems.push(setupFeeItem);
        console.log(`[Embroidery Processor] 🛠️ group${groupId}添加Setup Fee:`, setupFeeItem);
      }
    }
    
    // 为该group的每个定制面生成一个费用项
    uniqueCustomSides.forEach((customSide, index) => {
      const feeItem = this.createEmbroideryFeeItem(
        groupId,
        draftOrderId,
        billingQuantity, 
        customSide, 
        index,
        productDetails,
        totalQuantity // 传递实际数量用于设置费用项数量
      );
      feeItems.push(feeItem);
    });
    
    return feeItems;
  }
  
  /**
   * 计算实际计费数量（绣花第一个区间有最小起订量）
   */
  calculateBillingQuantity(actualQuantity, priceRange) {
    // 绣花工艺：第一个区间（1-11）最小收费按11个计算
    if (priceRange.min === 1 && actualQuantity < 11) {
      return 11; // 最小起订量
    }
    return actualQuantity;
  }
  
  /**
   * 创建绣花 Setup Fee项
   */
  createSetupFeeItem(quantity, groupId, draftOrderId) {
    if (!this.needsSetupFee(quantity)) return null;
    
    return {
      id: SETUP_FEE_CONFIG.variantId, // 使用全局Setup Fee变体ID
      quantity: 1, // Setup Fee 始终是1个，不跟随主商品数量
      properties: {
        // 用户可见的属性（会在购物车页面显示）
        'Craft Type': 'Embroidery',
        'Description': 'One-time setup fee for embroidery (waived for orders of 12+ pcs)',
        
        // 内部管理属性（以下划线开头，不显示在购物车）
        '_custom_fee_type': 'EMBROIDERY',
        '_setup_fee': 'true',
        '_group_id': groupId, // 关键：group ID（主要分组标识）
        '_draft_order_id': draftOrderId, // 草稿单ID（用于兼容和追踪）
        '_billing_quantity': quantity,
        '_actual_quantity': 1, // Setup Fee 数量始终是1
        '_description': `Embroidery Setup Fee (applies to ${quantity} pcs)`,
        '_embroidery_specific': 'true',
        '_transfer_type': 'Embroidery',
        '_is_setup_fee': 'true' // 标识这是Setup Fee
      }
    };
  }

  /**
   * 创建单个绣花费用项
   */
  createEmbroideryFeeItem(groupId, draftOrderId, billingQuantity, customSide, sideIndex, productDetails, actualQuantity) {
    const variantId = this.getVariantIdByQuantity(billingQuantity);
    
    // 计算定制费数量：主商品数量 × 定制面数量（这个定制面只算1个面）
    // 每个面都会调用这个方法，所以每个费用项的数量就是主商品的总数量
    const customFeeQuantity = actualQuantity; // actualQuantity 已经是所有主商品的总数量
    
    // 生成详细描述
    const description = this.generateEmbroideryDescription(customSide, billingQuantity, productDetails, customFeeQuantity);
    const sideDisplayName = this.getCustomSideDisplayName(customSide);
    
    // 构建定制费商品的可见属性
    const visibleProperties = {
      // 用户可见的属性（会在购物车页面显示）
      'Custom Side': sideDisplayName,
      'Craft Type': 'Embroidery', 
      'Products': this.generateProductsSummary(productDetails),
      
      // 内部管理属性（以下划线开头，不显示在购物车）
      '_custom_fee_type': 'EMBROIDERY',
      '_group_id': groupId, // 关键：group ID（主要分组标识）
      '_draft_order_id': draftOrderId, // 草稿单ID（用于兼容和追踪）
      '_custom_side': customSide,
      '_billing_quantity': billingQuantity,
      '_actual_quantity': actualQuantity, // 记录实际商品数量
      '_custom_fee_quantity': customFeeQuantity, // 记录定制费数量
      '_description': description,
      '_side_index': sideIndex + 1,
      '_total_sides': productDetails.reduce((sum, p) => sum + p.customSides.length, 0),
      '_products_summary': this.generateProductsSummary(productDetails),
      '_embroidery_specific': 'true',
      '_transfer_type': 'Embroidery',
      '_is_custom_fee': 'true' // 标识这是定制费商品
    };
    
    return {
      id: variantId,
      quantity: customFeeQuantity, // 定制费数量 = 主商品数量（每个面都会有一个费用项）
      properties: visibleProperties
    };
  }
  
  /**
   * 生成绣花费用项描述
   */
  generateEmbroideryDescription(customSide, billingQuantity, productDetails, customFeeQuantity = null) {
    const sideDisplayName = this.getCustomSideDisplayName(customSide);
    const productCount = productDetails.length;
    const totalActualQty = productDetails.reduce((sum, p) => sum + p.quantity, 0);
    const groupId = productDetails[0]?.groupId || 'Unknown';
    
    let description = `Embroidery Custom Fee - ${sideDisplayName}`;
    
    // 添加定制费数量信息
    if (customFeeQuantity !== null) {
      description += ` (${customFeeQuantity} custom fees for ${totalActualQty} products)`;
      
      // 如果有最小计费量差异，也显示出来
      if (billingQuantity !== totalActualQty) {
        description += ` [minimum charge: ${billingQuantity} pcs]`;
      }
    } else {
      // 保持原有逻辑作为备用
      if (billingQuantity !== totalActualQty) {
        description += ` (${totalActualQty} pcs actual, ${billingQuantity} pcs minimum charge)`;
      } else {
        description += ` (${billingQuantity} pcs)`;
      }
    }
    
    // 添加商品信息
    if (productCount === 1) {
      description += ` | Product: ${productDetails[0].productTitle}`;
    } else {
      description += ` | ${productCount} mixed products`;
    }
    
    // 添加group信息
    description += ` | Group: ${groupId}`;
    
    return description;
  }
  
  /**
   * 生成商品汇总信息
   */
  generateProductsSummary(productDetails) {
    return productDetails.map(p => 
      `${p.productTitle} (${p.quantity} pcs)`
    ).join(', ');
  }
}

// 将处理器暴露到全局作用域
window.EmbroideryProcessor = EmbroideryProcessor;